import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { ChevronLeft, ChevronRight, type LucideIconData } from 'lucide-angular';

import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { EventDialogService } from '../../data/event-dialog.service';
import type { StoreEvent } from '../../data/events.model';
import { buildCalendarGrid, type CalendarDay } from '../../utils/event-calendar.util';
import { monthLabel, shiftMonth, WEEKDAY_INITIALS } from '../../utils/event-format.util';

/** Títulos que caben en una casilla de escritorio antes de resumir en "+N". */
const VISIBLE_PER_DAY = 2;

/**
 * Rejilla mensual. Recibe el mes y sus eventos, y pide el cambio de mes hacia
 * arriba —quien la usa es quien sabe pedir datos—.
 *
 * En escritorio cada casilla lista los títulos que caben. En móvil no cabe
 * ninguno, así que la casilla se reduce a puntos: es la única forma de que un
 * mes entero quepa en 360 px sin scroll horizontal.
 *
 * Pulsar un día con eventos abre el diálogo de eventos (D21), igual en móvil
 * que en escritorio: con varios, la vista día; con uno solo, su detalle
 * directamente. Ya no hay detalle bajo la rejilla ni día seleccionado.
 */
@Component({
  selector: 'app-events-calendar',
  imports: [IconComponent],
  templateUrl: './events-calendar.component.html',
  styleUrl: './events-calendar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class EventsCalendarComponent {
  month = input.required<string>();
  events = input.required<readonly StoreEvent[]>();

  readonly monthChange = output<string>();

  private readonly eventDialog = inject(EventDialogService);

  protected readonly weekdays = WEEKDAY_INITIALS;
  protected readonly chevronLeftIcon: LucideIconData = ChevronLeft;
  protected readonly chevronRightIcon: LucideIconData = ChevronRight;
  protected readonly visiblePerDay = VISIBLE_PER_DAY;

  protected readonly weeks = computed(() => buildCalendarGrid(this.month(), this.events()));

  protected readonly heading = computed(() => monthLabel(this.month()));

  /** Un día sin eventos está deshabilitado; la guarda cubre igualmente un clic que llegue. */
  protected openDay(day: CalendarDay): void {
    if (day.events.length === 0) return;
    this.eventDialog.openDay(day.key, day.events);
  }

  protected goToPreviousMonth(): void {
    this.changeMonth(-1);
  }

  protected goToNextMonth(): void {
    this.changeMonth(1);
  }

  private changeMonth(delta: number): void {
    this.monthChange.emit(shiftMonth(this.month(), delta));
  }

  /** Rótulo accesible de la casilla: el número solo no dice nada al lector. */
  protected dayAriaLabel(day: CalendarDay): string {
    const count = day.events.length;
    if (count === 0) return `${day.dayOfMonth}, sin eventos`;
    return `${day.dayOfMonth}, ${count} ${count === 1 ? 'evento' : 'eventos'}`;
  }

  protected hiddenCount(day: CalendarDay): number {
    return Math.max(0, day.events.length - VISIBLE_PER_DAY);
  }
}
